import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Edit, Trash2, X, Save, Plus, Image as ImageIcon, Trash, Upload, Loader } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const emptyForm = {
  name: '',
  description: '',
  duration: '',
  price: 0,
  category: 'day',
  inclusions: '',
  highlights: '',
  image_url: '',
  images: [],
  max_guests: 6,
  pricing_type: 'group',
};

const AdminPackages = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPackage, setEditingPackage] = useState(null); // null | 'new' | id
  const [editForm, setEditForm] = useState(emptyForm);
  const [newImageUrl, setNewImageUrl] = useState('');
  const [uploadingCover, setUploadingCover] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const coverInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  // Resolve relative `/api/uploads/...` paths to absolute backend URL for previews
  const resolveImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    if (url.startsWith('/api/')) return `${BACKEND_URL}${url}`;
    return url;
  };

  const uploadFile = async (file) => {
    const token = localStorage.getItem('adminToken');
    const fd = new FormData();
    fd.append('file', file);
    const res = await axios.post(`${API}/upload`, fd, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data',
      },
    });
    return res.data.url; // /api/uploads/<file>
  };

  const handleCoverFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    setUploadingCover(true);
    try {
      const url = await uploadFile(file);
      setEditForm((prev) => ({ ...prev, image_url: url }));
      toast.success('Cover image uploaded!');
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingCover(false);
      if (coverInputRef.current) coverInputRef.current.value = '';
    }
  };

  const handleGalleryFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploadingGallery(true);
    try {
      const uploaded = [];
      for (const f of files) {
        if (!f.type.startsWith('image/')) {
          toast.error(`Skipped ${f.name} (not an image)`);
          continue;
        }
        try {
          const url = await uploadFile(f);
          uploaded.push(url);
        } catch (err) {
          toast.error(`Failed: ${f.name}`);
        }
      }
      if (uploaded.length > 0) {
        setEditForm((prev) => ({ ...prev, images: [...prev.images, ...uploaded] }));
        toast.success(`Uploaded ${uploaded.length} image${uploaded.length > 1 ? 's' : ''}`);
      }
    } finally {
      setUploadingGallery(false);
      if (galleryInputRef.current) galleryInputRef.current.value = '';
    }
  };

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      const response = await axios.get(`${API}/packages`);
      setPackages(response.data);
    } catch (error) {
      console.error('Error fetching packages:', error);
      toast.error('Failed to load packages');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (pkg) => {
    setEditingPackage(pkg.id);
    setEditForm({
      name: pkg.name || '',
      description: pkg.description || '',
      duration: pkg.duration || '',
      price: pkg.price ?? 0,
      category: pkg.category || 'day',
      inclusions: (pkg.inclusions || []).join('\n'),
      highlights: (pkg.highlights || []).join('\n'),
      image_url: pkg.image_url || '',
      images: Array.isArray(pkg.images) ? [...pkg.images] : [],
      max_guests: pkg.max_guests ?? 6,
      pricing_type: pkg.pricing_type || 'group',
    });
    setNewImageUrl('');
  };

  const handleCreateNew = () => {
    setEditingPackage('new');
    setEditForm(emptyForm);
    setNewImageUrl('');
  };

  const handleAddImage = () => {
    const url = (newImageUrl || '').trim();
    if (!url) {
      toast.error('Please enter a valid image URL');
      return;
    }
    setEditForm({ ...editForm, images: [...editForm.images, url] });
    setNewImageUrl('');
  };

  const handleRemoveImage = (index) => {
    const updated = editForm.images.filter((_, i) => i !== index);
    setEditForm({ ...editForm, images: updated });
  };

  const handleSaveEdit = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      // Use first gallery image as primary image_url if image_url is empty
      const primaryImage = editForm.image_url?.trim() || editForm.images[0] || '';
      if (!primaryImage) {
        toast.error('Please add at least one image (cover or gallery)');
        return;
      }
      const updateData = {
        name: editForm.name.trim(),
        description: editForm.description.trim(),
        duration: editForm.duration.trim(),
        price: parseFloat(editForm.price) || 0,
        category: editForm.category,
        max_guests: parseInt(editForm.max_guests) || 1,
        inclusions: editForm.inclusions.split('\n').map(i => i.trim()).filter(Boolean),
        highlights: editForm.highlights.split('\n').map(h => h.trim()).filter(Boolean),
        image_url: primaryImage,
        images: editForm.images.filter(Boolean),
        pricing_type: editForm.pricing_type || 'group',
      };

      if (editingPackage === 'new') {
        await axios.post(`${API}/packages`, updateData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Package created successfully!');
      } else {
        await axios.put(`${API}/packages/${editingPackage}`, updateData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Package updated successfully!');
      }
      setEditingPackage(null);
      fetchPackages();
    } catch (error) {
      console.error('Error saving package:', error);
      toast.error(error?.response?.data?.detail || 'Failed to save package');
    }
  };

  const handleDelete = async (packageId) => {
    if (!window.confirm('Are you sure you want to delete this package?')) return;

    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${API}/admin/packages/${packageId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Package deleted successfully');
      fetchPackages();
    } catch (error) {
      console.error('Error deleting package:', error);
      toast.error('Failed to delete package');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="font-body text-muted-foreground">Loading packages...</p>
      </div>
    );
  }

  return (
    <div data-testid="admin-packages">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-3xl text-navy">Package Management</h1>
        <button
          onClick={handleCreateNew}
          data-testid="create-package-btn"
          className="bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-3 rounded-lg font-body font-semibold flex items-center space-x-2 transition-all shadow-md"
        >
          <Plus size={18} />
          <span>New Package</span>
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted">
              <tr>
                <th className="text-left font-body text-sm font-semibold text-navy px-6 py-4">Name</th>
                <th className="text-left font-body text-sm font-semibold text-navy px-6 py-4">Category</th>
                <th className="text-left font-body text-sm font-semibold text-navy px-6 py-4">Duration</th>
                <th className="text-left font-body text-sm font-semibold text-navy px-6 py-4">Price (INR)</th>
                <th className="text-left font-body text-sm font-semibold text-navy px-6 py-4">Images</th>
                <th className="text-left font-body text-sm font-semibold text-navy px-6 py-4">Max Guests</th>
                <th className="text-right font-body text-sm font-semibold text-navy px-6 py-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {packages.map((pkg, index) => {
                const galleryCount = (pkg.images?.length || 0) + (pkg.image_url ? 1 : 0);
                return (
                  <tr key={pkg.id} className={index % 2 === 0 ? 'bg-white' : 'bg-sand/30'}>
                    <td className="font-body px-6 py-4">{pkg.name}</td>
                    <td className="font-body px-6 py-4">
                      <span className="px-3 py-1 bg-navy/10 text-navy rounded-full text-xs font-semibold uppercase">
                        {pkg.category}
                      </span>
                    </td>
                    <td className="font-body px-6 py-4">{pkg.duration}</td>
                    <td className="px-6 py-4 price-luxe-bold text-emerald-600 text-xl">
                      {pkg.price === 0 ? 'FREE' : `₹${pkg.price.toLocaleString()}`}
                      <span
                        className={`ml-2 inline-block align-middle px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${
                          pkg.pricing_type === 'per_person'
                            ? 'bg-amber-100 text-amber-800'
                            : 'bg-emerald-100 text-emerald-800'
                        }`}
                        data-testid={`pricing-type-badge-${pkg.id}`}
                      >
                        {pkg.pricing_type === 'per_person' ? '/ person' : 'group'}
                      </span>
                    </td>
                    <td className="font-body px-6 py-4">
                      <span className="inline-flex items-center space-x-1 px-2 py-1 bg-emerald-50 text-emerald-700 rounded-md text-xs font-semibold">
                        <ImageIcon size={14} />
                        <span>{galleryCount}</span>
                      </span>
                    </td>
                    <td className="font-body px-6 py-4">{pkg.max_guests}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleEdit(pkg)}
                          className="p-2 hover:bg-muted rounded-lg transition-colors"
                          title="Edit"
                          data-testid={`edit-package-${pkg.id}`}
                        >
                          <Edit size={18} className="text-navy" />
                        </button>
                        <button
                          onClick={() => handleDelete(pkg.id)}
                          className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 size={18} className="text-red-500" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit / Create Modal */}
      {editingPackage && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[92vh] overflow-y-auto">
            <div className="sticky top-0 bg-emerald-700 p-6 flex items-center justify-between z-10">
              <h2 className="font-heading text-2xl text-amber-300">
                {editingPackage === 'new' ? 'Create New Package' : 'Edit Package'}
              </h2>
              <button
                onClick={() => setEditingPackage(null)}
                className="text-white hover:text-amber-300"
                data-testid="close-edit-modal"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-5">
              {/* Name */}
              <div>
                <label className="block font-body text-sm font-medium text-navy mb-2">
                  Package Name *
                </label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="w-full border border-border rounded-lg px-4 py-2 font-body focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                  data-testid="edit-name-input"
                />
              </div>

              {/* Price + Duration + Max Guests */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block font-body text-sm font-medium text-navy mb-2">
                    Price (INR) *
                  </label>
                  <input
                    type="number"
                    value={editForm.price}
                    onChange={(e) => setEditForm({ ...editForm, price: e.target.value })}
                    className="w-full border border-border rounded-lg px-4 py-2 price-luxe-bold text-lg"
                    data-testid="edit-price-input"
                  />
                </div>
                <div>
                  <label className="block font-body text-sm font-medium text-navy mb-2">
                    Duration *
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. 2 Days / 1 Night"
                    value={editForm.duration}
                    onChange={(e) => setEditForm({ ...editForm, duration: e.target.value })}
                    className="w-full border border-border rounded-lg px-4 py-2 font-body"
                  />
                </div>
                <div>
                  <label className="block font-body text-sm font-medium text-navy mb-2">
                    Max Guests
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={editForm.max_guests}
                    onChange={(e) => setEditForm({ ...editForm, max_guests: e.target.value })}
                    className="w-full border border-border rounded-lg px-4 py-2 font-body"
                  />
                </div>
              </div>

              {/* Category */}
              <div>
                <label className="block font-body text-sm font-medium text-navy mb-2">
                  Category
                </label>
                <select
                  value={editForm.category}
                  onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                  className="w-full border border-border rounded-lg px-4 py-2 font-body"
                >
                  <option value="day">Day</option>
                  <option value="weekend">Weekend</option>
                  <option value="extended">Extended</option>
                </select>
              </div>

              {/* Pricing Type — Group (fixed) vs Per Person (scales with guests) */}
              <div>
                <label className="block font-body text-sm font-medium text-navy mb-2">
                  Pricing Type *
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <label
                    className={`cursor-pointer border-2 rounded-lg p-4 transition-all ${
                      editForm.pricing_type === 'group'
                        ? 'border-emerald-600 bg-emerald-50 shadow-md'
                        : 'border-border bg-white hover:border-emerald-300'
                    }`}
                    data-testid="pricing-type-group-label"
                  >
                    <input
                      type="radio"
                      name="pricing_type"
                      value="group"
                      checked={editForm.pricing_type === 'group'}
                      onChange={(e) => setEditForm({ ...editForm, pricing_type: e.target.value })}
                      className="mr-2 accent-emerald-600"
                      data-testid="pricing-type-group-radio"
                    />
                    <span className="font-body font-semibold text-navy">Group Package</span>
                    <p className="text-xs text-muted-foreground mt-1 ml-6">
                      Fixed total price for the whole group — does <b>not</b> change with number of guests.
                    </p>
                  </label>

                  <label
                    className={`cursor-pointer border-2 rounded-lg p-4 transition-all ${
                      editForm.pricing_type === 'per_person'
                        ? 'border-emerald-600 bg-emerald-50 shadow-md'
                        : 'border-border bg-white hover:border-emerald-300'
                    }`}
                    data-testid="pricing-type-per-person-label"
                  >
                    <input
                      type="radio"
                      name="pricing_type"
                      value="per_person"
                      checked={editForm.pricing_type === 'per_person'}
                      onChange={(e) => setEditForm({ ...editForm, pricing_type: e.target.value })}
                      className="mr-2 accent-emerald-600"
                      data-testid="pricing-type-per-person-radio"
                    />
                    <span className="font-body font-semibold text-navy">Per Person Package</span>
                    <p className="text-xs text-muted-foreground mt-1 ml-6">
                      Price scales with number of guests — total = price × guests.
                    </p>
                  </label>
                </div>
              </div>

              {/* Cover Image */}
              <div>
                <label className="block font-body text-sm font-medium text-navy mb-2">
                  Cover Image *
                </label>

                <div className="flex items-stretch space-x-2 mb-2">
                  <input
                    type="url"
                    value={editForm.image_url}
                    onChange={(e) => setEditForm({ ...editForm, image_url: e.target.value })}
                    className="flex-1 border border-border rounded-lg px-4 py-2 font-body"
                    placeholder="Paste image URL OR upload from device →"
                    data-testid="edit-cover-image-input"
                  />
                  <button
                    type="button"
                    onClick={() => coverInputRef.current?.click()}
                    disabled={uploadingCover}
                    className="bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-white px-4 rounded-lg font-body font-semibold flex items-center space-x-1 whitespace-nowrap"
                    data-testid="upload-cover-btn"
                  >
                    {uploadingCover ? (
                      <Loader size={18} className="animate-spin" />
                    ) : (
                      <Upload size={18} />
                    )}
                    <span>{uploadingCover ? 'Uploading…' : 'Upload'}</span>
                  </button>
                  <input
                    ref={coverInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleCoverFileSelect}
                    className="hidden"
                    data-testid="cover-file-input"
                  />
                </div>
                <p className="text-xs text-muted-foreground mb-2">
                  📱 Tap "Upload" to pick from your phone gallery / laptop. Supported: JPG, PNG, WEBP, GIF (max 10 MB).
                </p>

                {editForm.image_url && (
                  <div className="mt-3 relative">
                    <img
                      src={resolveImageUrl(editForm.image_url)}
                      alt="Cover Preview"
                      className="w-full h-56 object-cover rounded-lg shadow-lg"
                      onError={(e) => {
                        e.target.src =
                          'https://via.placeholder.com/800x600?text=Image+Not+Found';
                      }}
                    />
                    <div className="absolute top-2 right-2 bg-white px-3 py-1 rounded-full text-xs font-semibold text-emerald-700">
                      Cover
                    </div>
                  </div>
                )}
              </div>

              {/* Gallery Images (Multiple) */}
              <div className="bg-cream/50 p-4 rounded-lg border border-emerald-100">
                <div className="flex items-center justify-between mb-3">
                  <label className="block font-body text-sm font-semibold text-navy">
                    Gallery Images (Multiple)
                  </label>
                  <button
                    type="button"
                    onClick={() => galleryInputRef.current?.click()}
                    disabled={uploadingGallery}
                    className="bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-white px-4 py-2 rounded-lg font-body font-semibold flex items-center space-x-2 text-sm shadow-md"
                    data-testid="upload-gallery-btn"
                  >
                    {uploadingGallery ? (
                      <Loader size={16} className="animate-spin" />
                    ) : (
                      <Upload size={16} />
                    )}
                    <span>{uploadingGallery ? 'Uploading…' : 'Upload from device'}</span>
                  </button>
                  <input
                    ref={galleryInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleGalleryFileSelect}
                    className="hidden"
                    data-testid="gallery-file-input"
                  />
                </div>

                <p className="text-xs text-muted-foreground mb-3">
                  📱 Pick multiple photos from your phone gallery, OR paste an image URL below.
                </p>

                <div className="flex items-stretch space-x-2 mb-4">
                  <input
                    type="url"
                    value={newImageUrl}
                    onChange={(e) => setNewImageUrl(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddImage();
                      }
                    }}
                    placeholder="Or paste image URL and press Add"
                    className="flex-1 border border-border rounded-lg px-4 py-2 font-body"
                    data-testid="gallery-image-input"
                  />
                  <button
                    type="button"
                    onClick={handleAddImage}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 rounded-lg font-body font-semibold flex items-center space-x-1"
                    data-testid="add-gallery-image-btn"
                  >
                    <Plus size={18} />
                    <span>Add</span>
                  </button>
                </div>

                {editForm.images.length === 0 ? (
                  <p className="text-xs text-muted-foreground italic">
                    No gallery images added yet. Upload from device or paste URLs above.
                  </p>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {editForm.images.map((url, idx) => (
                      <div
                        key={`${url}-${idx}`}
                        className="relative group rounded-lg overflow-hidden shadow-md"
                        data-testid={`gallery-image-${idx}`}
                      >
                        <img
                          src={resolveImageUrl(url)}
                          alt={`Gallery ${idx + 1}`}
                          className="w-full h-32 object-cover"
                          onError={(e) => {
                            e.target.src =
                              'https://via.placeholder.com/400x300?text=Invalid';
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => handleRemoveImage(idx)}
                          className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition"
                          title="Remove"
                          data-testid={`remove-gallery-image-${idx}`}
                        >
                          <Trash size={14} />
                        </button>
                        <div className="absolute bottom-1 left-1 bg-black/60 text-white text-xs px-2 py-0.5 rounded">
                          #{idx + 1}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Description */}
              <div>
                <label className="block font-body text-sm font-medium text-navy mb-2">
                  Description *
                </label>
                <textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  rows="5"
                  placeholder="Describe the safari experience in detail..."
                  className="w-full border border-border rounded-lg px-4 py-2 font-body resize-y"
                  data-testid="edit-description-input"
                />
              </div>

              {/* Highlights */}
              <div>
                <label className="block font-body text-sm font-medium text-navy mb-2">
                  Highlights{' '}
                  <span className="text-xs text-muted-foreground">
                    (one per line)
                  </span>
                </label>
                <textarea
                  value={editForm.highlights}
                  onChange={(e) => setEditForm({ ...editForm, highlights: e.target.value })}
                  rows="5"
                  placeholder={`Tiger sighting opportunities\nExpert naturalist guide\nPhotography support\nLuxury jeep safari`}
                  className="w-full border border-border rounded-lg px-4 py-2 font-body resize-y"
                  data-testid="edit-highlights-input"
                />
              </div>

              {/* What's Included */}
              <div>
                <label className="block font-body text-sm font-medium text-navy mb-2">
                  What's Included{' '}
                  <span className="text-xs text-muted-foreground">
                    (one per line)
                  </span>
                </label>
                <textarea
                  value={editForm.inclusions}
                  onChange={(e) => setEditForm({ ...editForm, inclusions: e.target.value })}
                  rows="6"
                  placeholder={`Safari permits & gate fees\nPickup & drop from hotel\nMineral water & refreshments\nProfessional naturalist guide\nAll meals (breakfast, lunch, dinner)`}
                  className="w-full border border-border rounded-lg px-4 py-2 font-body resize-y"
                  data-testid="edit-inclusions-input"
                />
              </div>

              {/* Actions */}
              <div className="flex space-x-4 pt-2">
                <button
                  onClick={() => setEditingPackage(null)}
                  className="flex-1 bg-muted text-navy px-6 py-3 rounded-lg font-body font-semibold hover:bg-gray-200 transition"
                  data-testid="cancel-edit-btn"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveEdit}
                  data-testid="save-package-btn"
                  className="flex-1 bg-emerald-600 text-white px-6 py-3 rounded-lg font-body font-semibold hover:bg-emerald-700 flex items-center justify-center space-x-2 transition shadow-md"
                >
                  <Save size={20} />
                  <span>{editingPackage === 'new' ? 'Create Package' : 'Save Changes'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPackages;
